/**
 * Metadata Service
 * Handles audio metadata extraction using Essentia API
 */

// Request is now FormData, no interface needed

export interface AnalyzeAudioResponse {
  success: boolean;
  bpm?: number;
  key?: string;
  error?: string;
  message?: string;
}

class MetadataService {
  private apiBaseUrl: string;
  // Cache for metadata results to avoid re-analyzing the same file
  // Key: s3Key, Value: { result: AnalyzeAudioResponse, timestamp: number }
  private metadataCache: Map<string, { result: AnalyzeAudioResponse; timestamp: number }> = new Map();
  // Cache expiration time: 1 hour (3600000 ms)
  private readonly CACHE_EXPIRY_MS = 60 * 60 * 1000;

  constructor() {
    // Get API URL from environment or use default
    let baseUrl = 
      import.meta.env.VITE_META_API_URL || 
      'http://localhost:3002'; // Default to Meta API port
    
    // Remove trailing slashes
    baseUrl = baseUrl.replace(/\/+$/, '');
    
    // Fix common URL issues:
    // If URL contains www.fyle-cloud.com/fyle-meta-api, extract just the vercel.app domain
    if (baseUrl.includes('fyle-meta-api.vercel.app')) {
      // Extract the vercel.app domain (remove any path before it)
      const match = baseUrl.match(/(https?:\/\/)?([^\/]*fyle-meta-api\.vercel\.app)/);
      if (match) {
        baseUrl = match[2];
      }
    }
    
    // Remove /api suffix if present (we'll add it explicitly)
    baseUrl = baseUrl.replace(/\/api$/, '');
    
    // Ensure it's a valid URL (not a path)
    if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
      // If it's not a full URL, assume it's a domain and add https://
      baseUrl = `https://${baseUrl}`;
    }
    
    this.apiBaseUrl = baseUrl;
    
    console.log('✅ Metadata API configured:', this.apiBaseUrl);
  }


  /**
   * Analyze audio file from URL (after upload to S3/CDN)
   * Uses the Fyle Meta API (Essentia-based)
   * Caches results by s3Key to avoid re-analyzing the same file
   */
  async analyzeAudioFromUrl(url: string, userId?: string, s3Key?: string, bypassCache: boolean = false): Promise<AnalyzeAudioResponse> {
    // Generate unique request ID for tracking with timestamp and random hash
    const timestamp = Date.now();
    const randomHash = Math.random().toString(36).substring(2, 15);
    const requestId = `req_${timestamp}_${randomHash}`;
    
    try {
      // CRITICAL: Always bypass cache for new uploads to prevent stale data
      // If bypassCache is true OR if this is a new upload (no s3Key in cache), force fresh analysis
      const shouldBypassCache = bypassCache || !s3Key;
      
      if (shouldBypassCache) {
        // Clear cache for this s3Key to ensure fresh analysis
        if (s3Key && this.metadataCache.has(s3Key)) {
          this.metadataCache.delete(s3Key);
          console.log('🗑️ [MetaAPI] Cleared cache for s3Key (bypassCache=true)', {
            requestId: requestId,
            s3Key: s3Key.substring(0, 50),
            s3KeyLength: s3Key.length
          });
        }
        console.log('🔄 [MetaAPI] Bypassing cache - will get fresh analysis', {
          requestId: requestId,
          s3Key: s3Key || 'not provided',
          s3KeyLength: s3Key?.length || 0,
          timestamp: timestamp,
          bypassCache: bypassCache
        });
      } else if (s3Key) {
        // Check cache only if bypassCache is false AND s3Key is provided
        const cached = this.metadataCache.get(s3Key);
        if (cached) {
          const age = Date.now() - cached.timestamp;
          if (age < this.CACHE_EXPIRY_MS) {
            console.log('💾 [MetaAPI] Returning cached metadata result', {
              requestId: requestId,
              s3Key: s3Key.substring(0, 50),
              bpm: cached.result.bpm,
              key: cached.result.key,
              cacheAge: `${Math.round(age / 1000)}s`
            });
            return cached.result;
          } else {
            // Cache expired, remove it
            console.log('⏰ [MetaAPI] Cache expired, removing old entry', {
              requestId: requestId,
              s3Key: s3Key.substring(0, 50),
              age: `${Math.round(age / 1000)}s`
            });
            this.metadataCache.delete(s3Key);
          }
        }
      }
      
      console.log('🎵 [MetaAPI] Starting audio metadata extraction from URL', {
        requestId: requestId,
        url: url.substring(0, 200) + (url.length > 200 ? '...' : ''),
        urlLength: url.length,
        s3Key: s3Key || 'not provided',
        s3KeyLength: s3Key?.length || 0,
        userId: userId || 'not provided',
        bypassCache: bypassCache,
        timestamp: new Date().toISOString()
      });
      
      if (!url || !url.trim()) {
        throw new Error('URL is required');
      }
      
      // Get userId from localStorage or use default
      const currentUserId = userId || this.getCurrentUserId();
      
      // Construct API URL
      let apiUrl = this.apiBaseUrl;
      apiUrl = apiUrl.replace(/\/+$/, '');
      if (!apiUrl.endsWith('/api/analyze-from-url')) {
        apiUrl = apiUrl.replace(/\/api$/, '');
        apiUrl = `${apiUrl}/api/analyze-from-url`;
      }
      
      console.log('🔗 [MetaAPI] Calling Meta API (from URL)', {
        requestId: requestId,
        apiUrl: apiUrl,
        s3Key: s3Key || 'not provided',
        s3KeyFull: s3Key, // Log full s3Key
        s3KeyLength: s3Key?.length || 0,
        urlLength: url.length,
        bypassCache: bypassCache
      });
      
      // CRITICAL: Send s3Key and bypassCache flag to backend
      // Backend should use s3Key for caching, not URL
      // If bypassCache=true, backend should always analyze fresh
      console.log('📤 [MetaAPI] Request body', {
        requestId: requestId,
        urlLength: url.length,
        urlPreview: url.substring(0, 200),
        s3Key: s3Key || 'not provided',
        s3KeyFull: s3Key, // Log full s3Key
        s3KeyLength: s3Key?.length || 0,
        userId: currentUserId,
        bypassCache: bypassCache
      });
      
      // CRITICAL: DO NOT modify the signed URL - it's cryptographically signed
      // Adding query parameters would invalidate the signature (HTTP 403)
      // The s3Key is already unique (UUID + timestamp), so each file gets its own analysis
      // Cache-busting is handled via requestId and timestamp in the request body
      
      // Call API with JSON body
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url: url, // Original signed URL - DO NOT modify
          userId: currentUserId,
          s3Key: s3Key || undefined, // CRITICAL: Always send s3Key for proper caching
          bypassCache: bypassCache || false, // Tell backend to bypass cache if needed
          requestId: requestId, // Add requestId for tracking and cache-busting
          timestamp: timestamp, // Add timestamp for cache-busting (in body, not URL)
        }),
      });
      
      console.log('📡 [MetaAPI] Response received', {
        requestId: requestId,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        s3Key: s3Key || 'not provided'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: errorText || `HTTP ${response.status}` };
        }
        
        console.error('❌ [MetaAPI] API Error', {
          requestId: requestId,
          status: response.status,
          errorData: errorData,
          s3Key: s3Key || 'not provided'
        });
        throw new Error(
          errorData.detail || 
          errorData.message || 
          errorData.error || 
          `API request failed with status ${response.status}: ${errorText.substring(0, 200)}`
        );
      }
      
      // Parse response
      const apiResult = await response.json();
      
      console.log('📥 [MetaAPI] Full API Response:', {
        requestId: requestId,
        fullResponse: apiResult,
        status: apiResult.status,
        bpm: apiResult.bpm,
        key: apiResult.key,
        key_scale: apiResult.key_scale,
        confidence: apiResult.confidence,
        s3Key: s3Key || 'not provided',
        urlUsed: url.substring(0, 150)
      });
      
      // Convert API response to our format
      if (apiResult.status === 'completed' && (apiResult.bpm || apiResult.key)) {
        // Use raw BPM from API - NO CORRECTION
        // The MetaAPI should return correct values, we trust them
        const rawBpm = apiResult.bpm;
        const rawKey = apiResult.key;
        
        console.log('📊 [MetaAPI] Raw API response (NO CORRECTION):', {
          requestId,
          rawBpm: rawBpm,
          rawKey: rawKey,
          bpmType: typeof rawBpm,
          fullResponse: {
            status: apiResult.status,
            bpm: apiResult.bpm,
            key: apiResult.key,
            key_scale: apiResult.key_scale,
            confidence: apiResult.confidence
          }
        });
        
        // Only basic validation - don't change the values
        let finalBpm = rawBpm;
        if (finalBpm && (finalBpm < 20 || finalBpm > 300)) {
          console.warn('⚠️ [MetaAPI] BPM seems unrealistic, but using API value:', {
            requestId,
            bpm: finalBpm
          });
        }
        
        const result: AnalyzeAudioResponse = {
          success: true,
          bpm: finalBpm ? Math.round(finalBpm) : undefined,
          key: rawKey || undefined,
          message: 'Metadata extracted successfully',
        };
        
        // Store result in cache if s3Key is provided AND not bypassing cache
        if (s3Key && !bypassCache) {
          this.metadataCache.set(s3Key, {
            result: result,
            timestamp: Date.now()
          });
          console.log('💾 [MetaAPI] Result cached for future requests', {
            requestId: requestId,
            s3Key: s3Key,
            s3KeyLength: s3Key.length,
            bpm: result.bpm,
            key: result.key
          });
        } else if (bypassCache) {
          console.log('🚫 [MetaAPI] NOT caching result (bypassCache=true)', {
            requestId: requestId,
            s3Key: s3Key || 'not provided',
            bpm: result.bpm,
            key: result.key
          });
        }
        
        console.log('✅ [MetaAPI] Metadata extracted successfully', {
          requestId: requestId,
          bpm: result.bpm,
          key: result.key,
          s3Key: s3Key || 'not provided',
          originalBpm: apiResult.bpm,
          originalKey: apiResult.key
        });
        return result;
      } else {
        console.error('❌ [MetaAPI] Metadata extraction failed - invalid response', {
          requestId: requestId,
          status: apiResult.status,
          hasBpm: !!apiResult.bpm,
          hasKey: !!apiResult.key,
          s3Key: s3Key || 'not provided',
          apiResult: apiResult
        });
        throw new Error(apiResult.detail || apiResult.message || 'Metadata extraction failed - no BPM or Key returned');
      }
    } catch (error) {
      console.error('❌ [MetaAPI] Metadata extraction failed', {
        requestId: requestId,
        error: error instanceof Error ? error.message : 'Unknown error',
        s3Key: s3Key || 'not provided',
        stack: error instanceof Error ? error.stack : undefined
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        message: error instanceof Error ? error.message : 'Failed to extract metadata',
      };
    }
  }

  /**
   * Analyze audio file and extract BPM and Key (from File object - for backward compatibility)
   * Uses the Fyle Meta API (Essentia-based)
   * NOTE: This should only be used if the file is NOT yet uploaded to S3
   */
  async analyzeAudio(file: File, userId?: string): Promise<AnalyzeAudioResponse> {
    try {
      console.log('🎵 Starting audio metadata extraction for:', file.name, `(${file.size} bytes)`);
      
      // Validate file
      if (!file || !(file instanceof File)) {
        console.error('❌ Invalid file object:', file);
        throw new Error('Invalid file: file is not a File object');
      }
      
      if (file.size === 0) {
        console.error('❌ File is empty:', file.name);
        throw new Error('File is empty');
      }
      
      console.log('✅ File validated:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
      });

      // Get userId from localStorage or use default
      const currentUserId = userId || this.getCurrentUserId();

      // Use FormData to send file directly (as expected by the API)
      const formData = new FormData();
      
      // Append file with explicit filename (important for API)
      formData.append('audioFile', file, file.name); // Third parameter ensures filename is sent
      formData.append('userId', currentUserId); // Required for rate limiting
      
      // Log FormData contents (for debugging)
      console.log('📦 FormData prepared:', {
        hasAudioFile: formData.has('audioFile'),
        hasUserId: formData.has('userId'),
        userId: currentUserId,
      });

      // Construct API URL - ensure correct format
      let apiUrl = this.apiBaseUrl;
      
      // Remove trailing slash
      apiUrl = apiUrl.replace(/\/+$/, '');
      
      // Add /api/analyze if not already present
      if (!apiUrl.endsWith('/api/analyze')) {
        // Remove /api if present (we'll add it back)
        apiUrl = apiUrl.replace(/\/api$/, '');
        apiUrl = `${apiUrl}/api/analyze`;
      }
      
      console.log('🔗 Calling Meta API:', apiUrl);
      console.log('📋 Base URL:', this.apiBaseUrl);
      console.log('👤 User ID:', currentUserId);
      console.log('📁 File:', {
        name: file.name,
        size: file.size,
        type: file.type,
      });
      
      // Call API with FormData
      // IMPORTANT: Do NOT set Content-Type header - browser will set it automatically with boundary
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        // DO NOT set Content-Type - browser will add it with multipart/form-data boundary
      });
      
      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: errorText || `HTTP ${response.status}` };
        }
        
        console.error('❌ API Error:', response.status, errorData);
        throw new Error(
          errorData.detail || 
          errorData.message || 
          errorData.error || 
          `API request failed with status ${response.status}: ${errorText.substring(0, 200)}`
        );
      }

      // Parse response (API returns AnalysisResponse format)
      const apiResult = await response.json();
      
      console.log('📥 API Response:', apiResult);

      // Convert API response to our format
      // API format: { status, bpm, key, key_scale, confidence, ... }
      if (apiResult.status === 'completed' && (apiResult.bpm || apiResult.key)) {
        // ROBUST BPM PROCESSING: Validate and correct BPM intelligently
        let processedBpm = apiResult.bpm;
        if (processedBpm) {
          processedBpm = this.correctAndValidateBPM(processedBpm, `local_${Date.now()}`);
        }
        
        const result: AnalyzeAudioResponse = {
          success: true,
          bpm: processedBpm || undefined,
          key: apiResult.key || undefined, // Full key name (e.g., "C# minor")
          message: 'Metadata extracted successfully',
        };
        
        console.log('✅ Metadata extracted:', { bpm: result.bpm, key: result.key });
        return result;
      } else {
        throw new Error(apiResult.detail || apiResult.message || 'Metadata extraction failed - no BPM or Key returned');
      }
    } catch (error) {
      console.error('❌ Metadata extraction failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        message: error instanceof Error ? error.message : 'Failed to extract metadata',
      };
    }
  }

  /**
   * ROBUST BPM CORRECTION & VALIDATION
   * Intelligently corrects BPM detection errors (half-time/double-time issues)
   * Similar to how major SaaS platforms handle BPM detection
   */
  private correctAndValidateBPM(rawBpm: number, requestId: string): number {
    // Round to 2 decimals first for processing
    let bpm = Math.round(rawBpm * 100) / 100;
    
    // Generate candidate BPMs: check if we detected half-time or double-time
    // Most realistic range: 60-180 BPM (covers 95% of all music)
    const candidates: number[] = [];
    
    // Try different multipliers/dividers
    if (bpm < 60) {
      // Too slow - might be half-time detection error
      candidates.push(bpm * 2, bpm * 4);
    } else if (bpm > 180 && bpm <= 360) {
      // Too fast - might be double-time detection error
      candidates.push(bpm / 2, bpm / 4);
    } else if (bpm > 360) {
      // Way too fast - definitely error
      candidates.push(bpm / 2, bpm / 4, bpm / 8);
    }
    
    // Always include original
    candidates.push(bpm);
    
    // Score each candidate
    let bestBpm = bpm;
    let bestScore = -Infinity;
    
    for (const candidate of candidates) {
      const score = this.scoreBPMCandidate(candidate);
      if (score > bestScore) {
        bestScore = score;
        bestBpm = candidate;
      }
    }
    
    // Final validation: ensure BPM is in realistic range
    if (bestBpm < 50 || bestBpm > 220) {
      // If still out of range, use original but clamp it
      console.warn(`⚠️ [MetaAPI] BPM ${bestBpm} out of range, using clamped value`, {
        requestId,
        original: rawBpm,
        corrected: bestBpm,
      });
      bestBpm = Math.max(50, Math.min(220, bestBpm));
    }
    
    // Round to integer (BPM is typically whole numbers)
    const finalBpm = Math.round(bestBpm);
    
    if (finalBpm !== Math.round(rawBpm)) {
      console.log(`🎯 [MetaAPI] BPM corrected: ${rawBpm} → ${finalBpm}`, {
        requestId,
        candidates: candidates.map(c => Math.round(c)),
        chosen: finalBpm,
      });
    }
    
    return finalBpm;
  }

  /**
   * Score a BPM candidate - higher score = more likely to be correct
   * Based on music theory and empirical data from major platforms
   */
  private scoreBPMCandidate(bpm: number): number {
    let score = 0;
    
    // Ideal range: 60-180 BPM (covers most genres)
    if (bpm >= 60 && bpm <= 180) {
      score += 30; // Base score for realistic range
      
      // Most popular range: 80-160 BPM
      if (bpm >= 80 && bpm <= 160) {
        score += 20;
        
        // Sweet spot: 90-150 BPM (most commercial music)
        if (bpm >= 90 && bpm <= 150) {
          score += 15;
        }
      }
    } else if (bpm >= 50 && bpm < 60) {
      // Very slow but possible (ballads, ambient)
      score += 10;
    } else if (bpm > 180 && bpm <= 200) {
      // Fast but possible (hardcore, drum & bass)
      score += 10;
    } else {
      // Outside realistic range - heavy penalty
      score -= 50;
    }
    
    // Preference for round numbers (120, 130, 140 are more common than 127, 133, 147)
    if (bpm % 5 === 0) {
      score += 5;
      if (bpm % 10 === 0) {
        score += 5; // Extra points for multiples of 10
      }
    }
    
    return score;
  }

  /**
   * Get current user ID from auth store or generate a temporary one
   */
  private getCurrentUserId(): string {
    try {
      // Try to get user ID from auth store (Zustand store)
      if (typeof window !== 'undefined') {
        // Dynamically import to avoid circular dependencies
        // Check if we can access the auth store
        try {
          // Try to get from Supabase session directly (more reliable)
          const supabaseKey = Object.keys(localStorage).find(key => 
            key.startsWith('sb-') && key.includes('auth-token')
          );
          
          if (supabaseKey) {
            const authDataStr = localStorage.getItem(supabaseKey);
            if (authDataStr) {
              try {
                const authData = JSON.parse(authDataStr);
                if (authData?.currentSession?.user?.id) {
                  return authData.currentSession.user.id;
                }
              } catch {
                // Try alternative format
                try {
                  const session = JSON.parse(authDataStr);
                  if (session?.user?.id) {
                    return session.user.id;
                  }
                } catch {
                  // Ignore
                }
              }
            }
          }
          
          // Fallback: Check for stored user ID
          const storedUserId = localStorage.getItem('fyle_user_id');
          if (storedUserId) {
            return storedUserId;
          }
        } catch (e) {
          console.warn('Could not get user ID from auth:', e);
        }
      }
      
      // Generate a temporary user ID (will be replaced when user is authenticated)
      const tempUserId = `temp_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      if (typeof window !== 'undefined') {
        localStorage.setItem('fyle_user_id', tempUserId);
      }
      return tempUserId;
    } catch {
      // Fallback if localStorage not available
      return `temp_${Date.now()}`;
    }
  }

  /**
   * Analyze multiple audio files
   */
  async analyzeMultiple(files: File[]): Promise<Map<string, AnalyzeAudioResponse>> {
    const results = new Map<string, AnalyzeAudioResponse>();
    
    // Process files sequentially to avoid overwhelming the API
    for (const file of files) {
      const result = await this.analyzeAudio(file);
      results.set(file.name, result);
    }
    
    return results;
  }

  /**
   * Clear cache for a specific s3Key
   */
  clearCacheForS3Key(s3Key: string): void {
    if (this.metadataCache.has(s3Key)) {
      this.metadataCache.delete(s3Key);
      console.log('🗑️ [MetaAPI] Cache cleared for s3Key:', s3Key);
    }
  }

  /**
   * Clear all cached metadata
   */
  clearAllCache(): void {
    const size = this.metadataCache.size;
    this.metadataCache.clear();
    console.log(`🗑️ [MetaAPI] Cleared all cached metadata (${size} entries)`);
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: Array<{ s3Key: string; age: number }> } {
    const entries = Array.from(this.metadataCache.entries()).map(([s3Key, value]) => ({
      s3Key,
      age: Date.now() - value.timestamp
    }));
    return {
      size: this.metadataCache.size,
      entries
    };
  }
}

export const metadataService = new MetadataService();

